/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { classifyTier } from "@cosmos/airtable-data";
import { DirectoryCard } from "@cosmos/components";
import { useMessages } from "@epic-mw/i18n";

import { buildPartnerLinks, buildPartnerLogo, servicePartnerBadge } from "./buildPartnerCardProps";

import type { ServicePartnerBadgeAssets } from "./buildPartnerCardProps";
import type { ServicePartner } from "@cosmos/airtable-data";

export interface ServicePartnerCardProps {
	data: ServicePartner;
	/** Gold/Silver badge images (Service Partner detail `goldBadgeAsset` / `silverBadgeAsset`). */
	badgeAssets?: ServicePartnerBadgeAssets;
}

/**
 * Adapter wrapping `DirectoryCard` for a `ServicePartners` row.
 *
 * The badge label is a fixed "Service partner" chip; the tier (from
 * `2026 Badge Rollup`) drives the badge tone via `servicePartnerBadge` —
 * Gold/Silver get the Service Partner metallic gradients and, when
 * `badgeAssets` is supplied, the tier's badge image. The meta row stacks office
 * locations + languages; `Countries Supported` is a long list (25+) better
 * suited to search/filter than a location chip, so it's intentionally omitted
 * from the card face.
 *
 * The "Learn More" button links to the partner's detail page
 * (`<directory path>/<partner slug>`) via `partner.learnMoreUrl`, which the
 * directory page builds from its own live path + the partner slug — so the
 * link is absent until the grid is rendered with a base path.
 */
export function ServicePartnerCard({ data: partner, badgeAssets }: ServicePartnerCardProps): React.JSX.Element {
	const messages = useMessages();
	const badgeLabel = messages["epic.cosmos.directory.card.partner.service"] ?? "Service partner";

	const tier = classifyTier(partner.badgeLevel);
	// No tier data → neutral fallback icon (Users) when there's no logo.
	const logo = buildPartnerLogo(partner.logoUrl, tier);

	const { buttons, meta } = buildPartnerLinks(
		{ learnMoreUrl: partner.learnMoreUrl, languages: partner.languages },
		{
			learnMore: messages["epic.cosmos.plugins.learnMore"] ?? "Learn More",
		},
		{ officeLocation: partner.officeLocations.length > 0 ? partner.officeLocations.join(", ") : undefined },
	);

	return (
		<DirectoryCard
			name={partner.name}
			badge={servicePartnerBadge(tier, badgeLabel, badgeAssets)}
			logo={logo}
			meta={meta}
			primaryTags={partner.industries}
			secondaryTags={partner.supportType}
			buttons={buttons}
		/>
	);
}
